import { forwardRef } from "react";
import {
  flip,
  offset,
  shift,
  useFloating,
  autoUpdate,
  Placement,
  Strategy,
} from "@floating-ui/react";
import { Menu, Transition } from "@headlessui/react";
import { Icon } from "@zapier/design-system";

import { styled, useTheme } from "lib/theme";

import type { Theme } from "lib/theme/themes";

type MenuItemColor = "primary" | "danger";

type MenuItem = {
  title: string;
  onClick: (recordId: string) => boolean;
  color?: MenuItemColor;
};

export type MenuButtonProps = {
  menuItems: MenuItem[];
  placement?: Placement;
  isDisabled?: boolean;
};

export const MenuButton = forwardRef<HTMLDivElement, MenuButtonProps>(
  function MenuButton({ menuItems, placement, isDisabled = false }, ref) {
    const theme = useTheme();

    const { x, y, strategy, refs } = useFloating({
      placement,
      middleware: [offset(7), flip({ padding: 50 }), shift({ padding: 7 })],
      whileElementsMounted: autoUpdate,
    });

    return (
      <Menu ref={ref}>
        <Menu.Button
          as={StyledMenuButton}
          ref={refs.setReference}
          $color={theme.app.colors.gray}
          $size={36}
          type="button"
          disabled={isDisabled}
        >
          <Icon name="navMoreVert" size={24} />
        </Menu.Button>
        <Transition
          as={StyledTransition}
          ref={refs.setFloating}
          $strategy={strategy}
          $x={x}
          $y={y}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Menu.Items as={StyledMenuItems}>
            {menuItems.map((item) => (
              <Menu.Item
                as={StyledMenuItem}
                key={item.title}
                $color={item.color}
                onClick={item.onClick}
                type="button"
                disabled={isDisabled}
              >
                {item.title}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
    );
  }
);

const StyledMenuButton = styled.button<{
  $size: number;
  $color?: string;
}>`
  ${({ $size }) => `
    height: ${$size}px;
    width: ${$size}px;
  `}

  position: relative;
  z-index: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $color }) => $color && `color: ${$color};}`}

  border-radius: 6px;

  &:hover {
    border: 1px solid ${({ theme }) => theme.app.colors.lightGray};
  }

  &[data-headlessui-state="open"],
  &[data-headlessui-state="open"]:hover,
  &:active,
  &:focus,
  &:focus-within {
    color: ${({ theme }) => theme.app.colors.primary};
    border: 2px solid ${({ theme }) => theme.app.colors.primary};
  }

  cursor: pointer;
`;

const StyledTransition = styled.div<{
  $strategy: Strategy;
  $x: number | null;
  $y: number | null;
}>`
  position: ${({ $strategy }) => $strategy};
  top: ${({ $y }) => $y ?? 0}px;
  left: ${({ $x }) => $x ?? 0}px;
  width: max-content;
  z-index: 1;

  &.transition {
    transition-property: transform, opacity;
  }

  &.duration-100 {
    transition-duration: 100ms;
  }
  &.duration-75 {
    transition-duration: 75ms;
  }

  &.ease-out {
    transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }

  &.transform.scale-95 {
    transform: scale(0.95);
  }
  &.transform.scale-100 {
    transform: scale(1);
  }

  &.opacity-0 {
    opacity: 0;
  }
  &.opacity-100 {
    opacity: 1;
  }
`;

const StyledMenuItems = styled.div`
  --shadow-color: 0deg 0% 63%;
  --shadow-elevation-medium: 0.3px 0.5px 0.7px hsl(var(--shadow-color) / 0.36),
    0.8px 1.6px 2px -0.8px hsl(var(--shadow-color) / 0.36),
    2.1px 4.1px 5.2px -1.7px hsl(var(--shadow-color) / 0.36),
    5px 10px 12.6px -2.5px hsl(var(--shadow-color) / 0.36);

  min-width: 200px;

  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.app.colors.white};
  border-radius: 12px;
  box-shadow: var(--shadow-elevation-medium);
  padding: 10px;

  &:focus-visible {
    outline: none;
  }
`;

const StyledMenuItem = styled.button<{
  $color?: MenuItemColor;
}>`
  font-family: ${({ theme }) => theme.app.fontFamily};
  font-size: 16px;
  line-height: 20px;
  text-align: left;

  color: ${({ theme }) => theme.app.colors.text};

  padding: 10px 15px;
  border-radius: 6px;

  cursor: pointer;

  ${({ theme, $color }) => getMenuItemColors(theme, $color)}
`;

function getMenuItemColors(theme: Theme, color?: MenuItemColor) {
  if (color === "danger") {
    return `
      &[data-headlessui-state="active"],
      &:hover {
        color: ${theme.app.colors.danger};
        background-color: ${theme.app.colors.lightDanger};
      }

      &[data-headlessui-state="active"] {
        box-shadow: inset 0 0 0 2px ${theme.app.colors.danger};
      }
    `;
  }

  return `
    &[data-headlessui-state="active"],
    &:hover {
      color: ${theme.app.colors.primary};
      background-color: ${theme.app.colors.lightPrimary};
    }

    &[data-headlessui-state="active"] {
      box-shadow: inset 0 0 0 2px ${theme.app.colors.primary};
    }
  `;
}
